/**
 * Gallery Handler — centered modal with blurred backdrop.
 *
 * Desktop: centered card (80vw max 680px), blurred page backdrop.
 * Mobile:  centered card (calc(100vw - 24px)), same blur.
 *
 * Usage (hotels.js):
 *   const galleryHandler = createGalleryHandler(widget);
 *   galleryHandler.handleShowMedia(msg);   // msg: { images, title, ... }
 *   galleryHandler.dismissGallery();
 */

const STYLES_ID = 'ttp-gallery-handler-styles';

function injectStyles() {
  if (document.getElementById(STYLES_ID)) return;
  const s = document.createElement('style');
  s.id = STYLES_ID;
  s.textContent = `
/* ── Backdrop ── */
.ttp-gh-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2147483640;
  background: rgba(0, 0, 0, 0);
  backdrop-filter: blur(0px);
  -webkit-backdrop-filter: blur(0px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
  transition: background 0.3s ease, backdrop-filter 0.3s ease;
  pointer-events: none;
}
.ttp-gh-backdrop.ttp-gh-visible {
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  pointer-events: all;
}

/* ── Card ── */
.ttp-gh-card {
  position: relative;
  width: 80vw;
  max-width: 680px;
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.18), 0 4px 16px rgba(0, 0, 0, 0.10);
  display: flex;
  flex-direction: column;
  opacity: 0;
  transform: scale(0.94) translateY(10px);
  transition: opacity 0.3s cubic-bezier(0.23, 1, 0.32, 1),
              transform 0.3s cubic-bezier(0.23, 1, 0.32, 1);
  max-height: calc(100vh - 32px);
}
.ttp-gh-backdrop.ttp-gh-visible .ttp-gh-card {
  opacity: 1;
  transform: scale(1) translateY(0);
}

/* ── Image area ── */
.ttp-gh-img-wrap {
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 */
  background: #f0f0ee;
  overflow: hidden;
  flex-shrink: 0;
}
.ttp-gh-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.5s ease, transform 0.5s ease;
  transform: scale(1.03);
  z-index: 1;
}
.ttp-gh-img.ttp-gh-img-active {
  opacity: 1;
  transform: scale(1);
}
.ttp-gh-img-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0ee;
  z-index: 0;
}

/* Gradient + caption */
.ttp-gh-img-grad {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 50%;
  background: linear-gradient(to bottom, transparent, rgba(0,0,0,0.42));
  pointer-events: none;
  z-index: 2;
}
.ttp-gh-caption {
  position: absolute;
  bottom: 12px; left: 16px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(255,255,255,0.92);
  pointer-events: none;
  letter-spacing: 0.01em;
  z-index: 3;
}

/* Counter */
.ttp-gh-counter {
  position: absolute;
  top: 12px; right: 12px;
  background: rgba(0,0,0,0.32);
  color: #fff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 11px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 20px;
  letter-spacing: 0.05em;
  pointer-events: none;
  z-index: 3;
}

/* Close button */
.ttp-gh-close {
  position: absolute;
  top: 12px; left: 12px;
  width: 36px; height: 36px;
  padding: 0;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: none;
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.15s;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  z-index: 4;
}
.ttp-gh-close:hover { background: rgba(0,0,0,0.7); }

/* Prev / next arrows */
.ttp-gh-nav {
  position: absolute;
  top: 50%; transform: translateY(-50%);
  left: 12px; right: 12px;
  display: flex; justify-content: space-between;
  pointer-events: none;
  z-index: 4;
}
.ttp-gh-arr {
  width: 42px; height: 42px;
  padding: 0;
  background: rgba(255,255,255,0.95);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border: none; border-radius: 50%;
  color: #222;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  pointer-events: all;
  transition: background 0.15s, transform 0.15s;
  box-shadow: 0 2px 10px rgba(0,0,0,0.18);
}
.ttp-gh-arr:hover { background: #fff; transform: scale(1.06); }
.ttp-gh-arr:active { transform: scale(0.94); }

/* ── Body ── */
.ttp-gh-body {
  padding: 16px 20px 12px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
}
.ttp-gh-room-name {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 17px;
  font-weight: 500;
  color: #1a1a1a;
  line-height: 1.2;
}
.ttp-gh-price-wrap { text-align: right; flex-shrink: 0; }
.ttp-gh-price-lbl {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 10px;
  color: #aaa;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.ttp-gh-price {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 22px;
  font-weight: 500;
  color: #1a1a1a;
  line-height: 1.1;
}

/* ── Thumbnails ── */
.ttp-gh-thumbs {
  display: flex;
  gap: 7px;
  padding: 0 20px 12px;
  overflow-x: auto;
  scrollbar-width: none;
  flex-shrink: 0;
}
.ttp-gh-thumbs::-webkit-scrollbar { display: none; }
.ttp-gh-thumb {
  flex-shrink: 0;
  width: 58px; height: 40px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  opacity: 0.42;
  transition: opacity 0.2s, border-color 0.2s, transform 0.15s;
  background: #e8e8e6;
}
.ttp-gh-thumb img {
  width: 100%; height: 100%; object-fit: cover; display: block;
}
.ttp-gh-thumb.ttp-gh-thumb-active {
  opacity: 1;
  border-color: #1a1a1a;
}
.ttp-gh-thumb:hover:not(.ttp-gh-thumb-active) {
  opacity: 0.72;
  transform: translateY(-1px);
}

/* ── Dots ── */
.ttp-gh-dots {
  display: flex;
  gap: 5px;
  align-items: center;
  padding: 0 20px 4px;
  flex-shrink: 0;
}
.ttp-gh-dot {
  height: 2.5px;
  border-radius: 2px;
  background: #ddd;
  cursor: pointer;
  width: 16px;
  transition: all 0.25s ease;
}
.ttp-gh-dot.ttp-gh-dot-active {
  background: #1a1a1a;
  width: 28px;
}

/* ── Footer ── */
.ttp-gh-footer {
  padding: 8px 20px 20px;
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}
.ttp-gh-btn-book {
  flex: 1;
  padding: 13px 20px;
  background: #1a1a1a;
  color: #fff;
  border: none;
  border-radius: 12px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  letter-spacing: 0.02em;
  transition: background 0.15s, transform 0.1s;
}
.ttp-gh-btn-book:hover { background: #2d2d2d; }
.ttp-gh-btn-book:active { transform: scale(0.98); }

/* ── Mobile ── */
@media (max-width: 600px) {
  .ttp-gh-backdrop { padding: 12px; align-items: center; }
  .ttp-gh-card {
    width: calc(100vw - 24px);
    max-width: none;
    max-height: calc(100vh - 24px);
    border-radius: 18px;
  }
  .ttp-gh-img-wrap { padding-top: 62%; }
  .ttp-gh-room-name { font-size: 15px; }
  .ttp-gh-price { font-size: 18px; }
  .ttp-gh-thumb { width: 48px; height: 34px; }
  .ttp-gh-btn-book { padding: 12px 16px; font-size: 13px; }
}
`;
  document.head.appendChild(s);
}

// CSS-based icons (more reliable than SVG innerHTML)
const ICON_CLOSE = `<span style="display:block;position:relative;width:16px;height:16px;"><span style="position:absolute;top:50%;left:50%;width:16px;height:2px;background:#fff;border-radius:1px;transform:translate(-50%,-50%) rotate(45deg);"></span><span style="position:absolute;top:50%;left:50%;width:16px;height:2px;background:#fff;border-radius:1px;transform:translate(-50%,-50%) rotate(-45deg);"></span></span>`;
const ICON_PREV  = `<span style="display:block;width:12px;height:12px;border-left:3px solid #222;border-bottom:3px solid #222;transform:rotate(45deg);margin-left:4px;"></span>`;
const ICON_NEXT  = `<span style="display:block;width:12px;height:12px;border-right:3px solid #222;border-top:3px solid #222;transform:rotate(45deg);margin-right:4px;"></span>`;
const ICON_IMG   = `<span style="font-size:32px;color:#ccc;line-height:1;">&#x1F5BC;</span>`;

export function createGalleryHandler(widget, options = {}) {
  let backdrop   = null;
  let card       = null;
  let images     = [];
  let cur        = 0;
  let autoTimer  = null;
  let _dismissed = false;

  // DOM refs inside card
  let imgEls     = [];
  let thumbEls   = [];
  let dotEls     = [];
  let captionEl  = null;
  let counterEl  = null;
  let nameEl     = null;
  let priceEl    = null;

  // Widget z-index state
  let _savedHostZIndex = null;

  // ── Widget z-index management while gallery is open ──────────────
  function _minimizeWidget() {
    try {
      const host = widget.shadowRoot?.host;
      if (host) {
        _savedHostZIndex = host.style.zIndex || null;
        host.style.zIndex = String(2147483641); // one above backdrop so widget stays visible
      }
    } catch (e) { /* best-effort */ }
  }

  function _restoreWidget() {
    try {
      const host = widget.shadowRoot?.host;
      if (host) {
        host.style.zIndex = _savedHostZIndex || '';
        _savedHostZIndex = null;
      }
    } catch (e) { /* best-effort */ }
  }

  // ── Build DOM ─────────────────────────────────────────────────────
  function _buildCard(msg) {
    images = msg.images || [];
    cur    = 0;
    imgEls = []; thumbEls = []; dotEls = [];

    // Card
    card = document.createElement('div');
    card.className = 'ttp-gh-card';
    card.addEventListener('click', e => e.stopPropagation());

    // — Image wrap —
    const imgWrap = document.createElement('div');
    imgWrap.className = 'ttp-gh-img-wrap';

    // placeholder (shows while images load)
    const placeholder = document.createElement('div');
    placeholder.className = 'ttp-gh-img-placeholder';
    placeholder.innerHTML = ICON_IMG;
    imgWrap.appendChild(placeholder);

    // actual <img> elements (one per image, crossfade)
    images.forEach((img, i) => {
      const el = document.createElement('img');
      el.className = 'ttp-gh-img' + (i === 0 ? ' ttp-gh-img-active' : '');
      el.src   = img.url;
      el.alt   = img.caption || '';
      el.draggable = false;
      el.onload = () => { placeholder.style.display = 'none'; };
      imgWrap.appendChild(el);
      imgEls.push(el);
    });

    // gradient + caption
    const grad = document.createElement('div');
    grad.className = 'ttp-gh-img-grad';
    imgWrap.appendChild(grad);

    captionEl = document.createElement('div');
    captionEl.className = 'ttp-gh-caption';
    captionEl.textContent = images[0]?.caption || '';
    imgWrap.appendChild(captionEl);

    // counter badge
    counterEl = document.createElement('div');
    counterEl.className = 'ttp-gh-counter';
    counterEl.textContent = images.length > 1 ? `1 / ${images.length}` : '';
    if (images.length <= 1) counterEl.style.display = 'none';
    imgWrap.appendChild(counterEl);

    // close button
    const closeBtn = document.createElement('button');
    closeBtn.className = 'ttp-gh-close';
    closeBtn.setAttribute('aria-label', 'Close gallery');
    closeBtn.innerHTML = ICON_CLOSE;
    closeBtn.addEventListener('click', dismissGallery);
    imgWrap.appendChild(closeBtn);

    // prev / next arrows
    if (images.length > 1) {
      const nav = document.createElement('div');
      nav.className = 'ttp-gh-nav';

      const prev = document.createElement('button');
      prev.className = 'ttp-gh-arr';
      prev.setAttribute('aria-label', 'Previous');
      prev.innerHTML = ICON_PREV;
      prev.addEventListener('click', () => { _goTo(cur - 1); _resetAuto(); });

      const next = document.createElement('button');
      next.className = 'ttp-gh-arr';
      next.setAttribute('aria-label', 'Next');
      next.innerHTML = ICON_NEXT;
      next.addEventListener('click', () => { _goTo(cur + 1); _resetAuto(); });

      nav.appendChild(prev);
      nav.appendChild(next);
      imgWrap.appendChild(nav);
    }

    card.appendChild(imgWrap);

    // — Body (room name + price) —
    const body = document.createElement('div');
    body.className = 'ttp-gh-body';

    nameEl = document.createElement('div');
    nameEl.className = 'ttp-gh-room-name';
    nameEl.textContent = msg.title || '';
    body.appendChild(nameEl);

    if (msg.price) {
      const pw = document.createElement('div');
      pw.className = 'ttp-gh-price-wrap';
      const pl = document.createElement('div');
      pl.className = 'ttp-gh-price-lbl';
      pl.textContent = options.priceLabel || '';
      priceEl = document.createElement('div');
      priceEl.className = 'ttp-gh-price';
      priceEl.textContent = msg.price;
      pw.appendChild(pl);
      pw.appendChild(priceEl);
      body.appendChild(pw);
    }

    card.appendChild(body);

    // — Thumbnails (only if > 1 image) —
    if (images.length > 1) {
      const thumbStrip = document.createElement('div');
      thumbStrip.className = 'ttp-gh-thumbs';

      images.forEach((img, i) => {
        const t = document.createElement('div');
        t.className = 'ttp-gh-thumb' + (i === 0 ? ' ttp-gh-thumb-active' : '');
        const ti = document.createElement('img');
        ti.src = img.url;
        ti.alt = img.caption || '';
        t.appendChild(ti);
        t.addEventListener('click', () => { _goTo(i); _resetAuto(); });
        thumbStrip.appendChild(t);
        thumbEls.push(t);
      });

      card.appendChild(thumbStrip);

      // — Dots —
      const dotsRow = document.createElement('div');
      dotsRow.className = 'ttp-gh-dots';
      images.forEach((_, i) => {
        const d = document.createElement('div');
        d.className = 'ttp-gh-dot' + (i === 0 ? ' ttp-gh-dot-active' : '');
        d.addEventListener('click', () => { _goTo(i); _resetAuto(); });
        dotsRow.appendChild(d);
        dotEls.push(d);
      });
      card.appendChild(dotsRow);
    }

    // — Footer (only if buttonText is provided) —
    const btnText = options.buttonText;
    if (btnText) {
      const footer = document.createElement('div');
      footer.className = 'ttp-gh-footer';

      const bookBtn = document.createElement('button');
      bookBtn.className = 'ttp-gh-btn-book';
      bookBtn.textContent = btnText;
      bookBtn.addEventListener('click', dismissGallery);
      footer.appendChild(bookBtn);

      card.appendChild(footer);
    }

    // touch/swipe
    _setupSwipe(card);

    return card;
  }

  // ── Navigation ────────────────────────────────────────────────────
  function _goTo(index) {
    if (images.length === 0) return;
    cur = ((index % images.length) + images.length) % images.length;

    imgEls.forEach((el, i)   => el.classList.toggle('ttp-gh-img-active',   i === cur));
    thumbEls.forEach((el, i) => el.classList.toggle('ttp-gh-thumb-active', i === cur));
    dotEls.forEach((el, i)   => el.classList.toggle('ttp-gh-dot-active',   i === cur));

    if (captionEl) captionEl.textContent = images[cur]?.caption || '';
    if (counterEl) counterEl.textContent = `${cur + 1} / ${images.length}`;
  }

  // ── Auto-rotate ───────────────────────────────────────────────────
  function _startAuto() {
    if (images.length < 2) return;
    _stopAuto();
    autoTimer = setInterval(() => _goTo(cur + 1), 4000);
  }

  function _stopAuto() {
    if (autoTimer) { clearInterval(autoTimer); autoTimer = null; }
  }

  function _resetAuto() { _stopAuto(); _startAuto(); }

  // ── Swipe / drag ──────────────────────────────────────────────────
  function _setupSwipe(el) {
    if (images.length <= 1) return;
    let startX = 0;
    const THRESHOLD = 45;

    el.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
    el.addEventListener('touchend', e => {
      const diff = e.changedTouches[0].clientX - startX;
      if (Math.abs(diff) > THRESHOLD) { _goTo(cur + (diff > 0 ? -1 : 1)); _resetAuto(); }
    });

    let mouseDown = false;
    el.addEventListener('mousedown', e => { mouseDown = true; startX = e.clientX; });
    el.addEventListener('mouseup', e => {
      if (!mouseDown) return;
      mouseDown = false;
      const diff = e.clientX - startX;
      if (Math.abs(diff) > THRESHOLD) { _goTo(cur + (diff > 0 ? -1 : 1)); _resetAuto(); }
    });
  }

  // ── Public: show ──────────────────────────────────────────────────
  function handleShowMedia(msg) {
    // dismiss any existing gallery first
    if (backdrop) _teardown();

    _dismissed = false;
    injectStyles();

    // Build backdrop
    backdrop = document.createElement('div');
    backdrop.className = 'ttp-gh-backdrop';
    backdrop.addEventListener('click', dismissGallery); // click outside = dismiss

    backdrop.appendChild(_buildCard(msg));
    document.body.appendChild(backdrop);

    // Animate in
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        backdrop.classList.add('ttp-gh-visible');
        _startAuto();
      });
    });

    // Esc key
    backdrop._escHandler = e => { if (e.key === 'Escape') dismissGallery(); };
    document.addEventListener('keydown', backdrop._escHandler);

    // Minimize voice widget
    _minimizeWidget();
  }

  // ── Public: dismiss ───────────────────────────────────────────────
  function dismissGallery() {
    if (_dismissed || !backdrop) return;
    _dismissed = true;
    _stopAuto();

    // Animate out
    backdrop.classList.remove('ttp-gh-visible');

    const el = backdrop;
    setTimeout(() => {
      if (el.parentNode) el.parentNode.removeChild(el);
      if (el._escHandler) document.removeEventListener('keydown', el._escHandler);
    }, 320);

    backdrop = null;
    card     = null;
    imgEls   = []; thumbEls = []; dotEls = [];

    _restoreWidget();
  }

  // ── Teardown (replace without animation) ─────────────────────────
  function _teardown() {
    _stopAuto();
    if (backdrop) {
      if (backdrop._escHandler) document.removeEventListener('keydown', backdrop._escHandler);
      if (backdrop.parentNode) backdrop.parentNode.removeChild(backdrop);
      backdrop = null; card = null;
      imgEls = []; thumbEls = []; dotEls = [];
    }
    _restoreWidget();
  }

  return { handleShowMedia, dismissGallery };
}